import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Select,
  Input,
  Space,
  message,
  Row,
  Col,
  Statistic,
  Divider,
  List,
  Badge,
  Descriptions,
  Tag,
  Tooltip,
  Tabs,
} from 'antd';
import { DatePicker } from 'antd';
import {
  ShoppingCartOutlined,
  PlusOutlined,
  DeleteOutlined,
  FileTextOutlined,
  BankOutlined,
  SearchOutlined,
  DownloadOutlined,
  PrinterOutlined,
  EyeOutlined,
  UserOutlined,
  MedicineBoxOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchSales, createSale } from '../store/slices/salesSlice';
import { fetchBranches } from '../store/slices/branchesSlice';
import { fetchMedicines } from '../store/slices/medicinesSlice';
import { useDebouncedSearch } from '../hooks/useDebounce';
import apiClient from '../config/axios';
import { formatForReceipt, formatForTable, getCurrentISTISO, isToday } from '../utils/timezone';
import { ENV_CONFIG } from '../config/environment';

const { Title } = Typography;
const { Option } = Select;

interface SaleItem {
  medicine_id: number;
  medicine_name: string;
  manufacturer?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  discount_percentage?: number;
  patient_name?: string | null;
  patient_id?: string | null;
}

interface Sale {
  sale_id: number;
  receipt_number: string;
  branch_id: number;
  branch_name: string;
  total_amount: number;
  notes?: string;
  sale_date: string;
  createdAt?: string;
  payment_method?: string;
  payment_methods?: Array<{
    payment_method: string;
    amount: number;
  }>;
  consultation_fee?: number;
  discount_amount?: number;
  sub_total?: number;
  customer_name?: string;
  customer_mobile?: string;
  patient_id?: string;
  patient_name?: string;
  sale_items?: SaleItem[];
}


interface SalesProps {
  branchId?: number;
}

const Sales: React.FC<SalesProps> = ({ branchId }) => {
  console.log('Sales component rendered with branchId:', branchId);
  
  const dispatch = useAppDispatch();
  const { sales, loading, pagination } = useAppSelector((state) => state.sales);
  const { branches } = useAppSelector((state) => state.branches);
  const { medicines } = useAppSelector((state) => state.medicines);
  
  // Main sales state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [cartItems, setCartItems] = useState<SaleItem[]>([]);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState<string>('');
  const [dateRange, setDateRange] = useState<[string | undefined, string | undefined]>([undefined, undefined]);
  
  // State for enhanced features
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [saleModalVisible, setSaleModalVisible] = useState(false);
  const [logoBase64, setLogoBase64] = useState<string>('');
  const [paymentTotals, setPaymentTotals] = useState<{ cash: number; card: number; upi: number }>({ cash: 0, card: 0, upi: 0 });
  const [totalRevenue, setTotalRevenue] = useState<number>(0);
  
  // New state for medicine sales aggregation
  const [medicineSalesSummary, setMedicineSalesSummary] = useState<Array<{
    medicine_name: string;
    total_quantity: number;
    total_amount: number;
    sale_count: number;
  }>>([]);
  
  // State for sliding panel
  const [isSlidingPanelOpen, setIsSlidingPanelOpen] = useState(true);
  const [activePanelTab, setActivePanelTab] = useState('sale-history');
  
  // Load logo as base64 on component mount
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch('/Gosval.png');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoBase64(reader.result as string);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error('Error loading logo:', error);
      }
    };
    loadLogo();
  }, []);
  
  // Debounce search text to avoid API calls on every keystroke
  const debouncedSearchText = useDebouncedSearch(searchText, 300);

  useEffect(() => {
    console.log('Sales useEffect triggered with branchId:', branchId);
    
    // Fetch sales - with branchId filter if provided, without filter if not provided (main seller mode)
    dispatch(fetchSales({ 
      page: 1, 
      limit: 10, 
      search: debouncedSearchText, 
      start_date: dateRange[0] || '', 
      end_date: dateRange[1] || '', 
      branchId
    }));
    dispatch(fetchBranches({}));
    dispatch(fetchMedicines({}));
  }, [dispatch, debouncedSearchText, branchId, dateRange]);

  // Fetch totals by payment method for selected date range
  useEffect(() => {
    const fetchTotals = async () => {
      try {
        const params: any = {};
        if (dateRange[0]) params['sale_from'] = dateRange[0];
        if (dateRange[1]) params['sale_to'] = dateRange[1];
        if (branchId) params['branch_id'] = branchId;

        const resp = await apiClient.get('/sales/reports/payment-method-totals', { params });
        const totals = resp.data?.data || {};
        setPaymentTotals({
          cash: Number(totals.cash_total || 0),
          card: Number(totals.card_total || 0),
          upi: Number(totals.upi_total || 0),
        });
      } catch (e) {
        console.error('Failed to fetch payment totals', e);
        setPaymentTotals({ cash: 0, card: 0, upi: 0 });
      }
    };

    fetchTotals();
  }, [dateRange, branchId]);


  // Function to aggregate medicine sales data
  const aggregateMedicineSales = useCallback(() => {
    const medicineMap = new Map<string, {
      medicine_name: string;
      total_quantity: number;
      total_amount: number;
      sale_count: number;
    }>();

    // Process all sales to aggregate medicine data
    sales.forEach(sale => {
      if (sale.sale_items && Array.isArray(sale.sale_items)) {
        sale.sale_items.forEach(item => {
          const medicineName = item.medicine_name;
          if (medicineName) {
            const existing = medicineMap.get(medicineName) || {
              medicine_name: medicineName,
              total_quantity: 0,
              total_amount: 0,
              sale_count: 0
            };
            
            existing.total_quantity += item.quantity || 0;
            existing.total_amount += item.total_price || 0;
            existing.sale_count += 1;
            
            medicineMap.set(medicineName, existing);
          }
        });
      }
    });

    // Convert map to array and sort by total quantity descending
    const summary = Array.from(medicineMap.values())
      .sort((a, b) => b.total_quantity - a.total_quantity);
    
    setMedicineSalesSummary(summary);
  }, [sales]);

  // Effect to aggregate medicine sales when sales data changes
  useEffect(() => {
    if (sales.length > 0) {
      aggregateMedicineSales();
    } else {
      setMedicineSalesSummary([]);
    }
  }, [sales, aggregateMedicineSales]);

  const handleNewSale = () => {
    setCartItems([]);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleClearCart = () => {
    setCartItems([]);
  };

  const handleAddToCart = (medicine: any, quantity: number) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.medicine_id === medicine.medicine_id);
      if (existingItem) {
        return prevItems.map(item =>
          item.medicine_id === medicine.medicine_id
            ? { ...item, quantity: item.quantity + quantity, total_price: (item.quantity + quantity) * item.unit_price }
            : item
        );
      } else {
        return [...prevItems, {
          medicine_id: medicine.medicine_id,
          medicine_name: medicine.medicine_name,
          quantity,
          unit_price: medicine.unit_price,
          total_price: quantity * medicine.unit_price,
        }];
      }
    });
  };

  const handleRemoveFromCart = (medicineId: number) => {
    setCartItems(prevItems => prevItems.filter(item => item.medicine_id !== medicineId));
  };

  const handleProcessSale = async () => {
    try {
      const values = await form.validateFields();
      if (cartItems.length === 0) {
        message.error('Please add items to cart');
        return;
      }

      await dispatch(createSale({
        branch_id: values.branch_id,
        receipt_number: values.receipt_number,
        notes: values.notes || '',
        payment_method: values.payment_method || 'cash',
        items: cartItems.map(item => ({
          medicine_id: item.medicine_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
        })),
      }));

      message.success('Sale processed successfully');
      setIsModalVisible(false);
      setCartItems([]);
      form.resetFields();
    } catch (error) {
      message.error('Please fill in all required fields');
    }
  };

  // View sale details
  const handleViewSale = async (saleId: number) => {
    try {
      // Fetch sale details with all related data
      const saleResponse = await apiClient.get(`/sales/${saleId}?populate[branch]=*&populate[created_by]=*&populate[sale_payments]=*&populate[sale_items]=*`);
      console.log('Sale details response:', saleResponse.data.data);
      
      // Fetch sale items with medicine details
      const saleItemsResponse = await apiClient.get(`/sale-items/sale/${saleId}?populate[branch_medicine][populate][medicine]=*`);
      console.log('Sale items response:', saleItemsResponse.data.data);
      
      // Transform the API response to match the expected structure
      const sale = saleResponse.data.data;
      const transformedSale = {
        sale_id: sale.id,
        receipt_number: sale.attributes?.receipt_number || '',
        total_amount: sale.attributes?.total_amount || 0,
        notes: sale.attributes?.notes || '',
        sale_date: sale.attributes?.sale_date || '',
        createdAt: sale.attributes?.createdAt || '',
        payment_method: sale.attributes?.payment_status || 'cash',
        payment_methods: (sale.attributes?.sale_payments?.data || []).map((payment: any) => ({
          payment_method: payment.attributes?.payment_method || 'cash',
          amount: payment.attributes?.amount || 0
        })),
        discount_amount: sale.attributes?.discount_amount || 0,
        sub_total: sale.attributes?.subtotal || sale.attributes?.sub_total || 0,
        branch_id: sale.attributes?.branch?.data?.id || 0,
        branch_name: sale.attributes?.branch?.data?.attributes?.branch_name || 'Unknown Branch',
        patient_name: sale.attributes?.patient_name || '',
        patient_id: sale.attributes?.patient_id || '',
        // Transform sale items with medicine and patient details
        sale_items: (saleItemsResponse.data.data || []).map((item: any) => {
          const medicineName = item.attributes?.branch_medicine?.data?.attributes?.medicine?.data?.attributes?.medicine_name;
          const manufacturer = item.attributes?.branch_medicine?.data?.attributes?.medicine?.data?.attributes?.generic_name;
          
          // Extract patient information from sale object with null checks
          const patientName = item.attributes?.sale?.data?.attributes?.patient_name || null;
          const patientId = item.attributes?.sale?.data?.attributes?.patient_id || null;
          
          return {
            medicine_name: medicineName || 'Unknown Medicine',
            manufacturer: manufacturer || '',
            quantity: item.attributes?.quantity || 0,
            unit_price: item.attributes?.unit_price || 0,
            total_price: item.attributes?.total_price || 0,
            discount_percentage: item.attributes?.discount_percentage || 0,
            patient_name: patientName,
            patient_id: patientId
          };
        })
      };
      
      console.log('Transformed sale:', transformedSale);
      console.log('Transformed sale items:', transformedSale.sale_items);
      
      setSelectedSale(transformedSale);
      setSaleModalVisible(true);
    } catch (error) {
      console.error('Error fetching sale details:', error);
      message.error('Failed to fetch sale details');
    }
  };

  // Download sale receipt
  const handleDownloadSale = async (sale: Sale) => {
    const receiptContent = await generateReceiptContent(sale);
    const blob = new Blob([receiptContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt_${sale.receipt_number}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    message.success('Receipt downloaded successfully');
  };

  // Print sale receipt
  const handlePrintSale = async (sale: Sale) => {
    console.log('Printing sale receipt. Logo state:', {
      hasLogoBase64: !!logoBase64,
      logoPreview: logoBase64?.substring(0, 50) + '...',
      currentOrigin: window.location.origin
    });
    const receiptContent = await generateReceiptContent(sale);
    console.log('Receipt content preview:', receiptContent.substring(0, 500));
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(receiptContent);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 200);
    }
  };

  // Generate receipt content
  const generateReceiptContent = async (sale: Sale) => {
    const receiptDate = formatForReceipt(new Date()); // Use current time for receipt generation
    const logoSrc = logoBase64 || (typeof window !== 'undefined' && window.location.origin ? `${window.location.origin}/Gosval.png` : '/Gosval.png');
    console.log('Logo loaded:', !!logoBase64, 'Fallback URL:', logoSrc);
    
    // Fetch branch details for receipt
    let branchDetails = {
      branch_name: ENV_CONFIG.DEFAULT_STORE_NAME,
      location: ENV_CONFIG.DEFAULT_LOCATION,
      phone: ENV_CONFIG.DEFAULT_CONTACT,
      email: ''
    };
    
    if (sale.branch_id) {
      try {
        const branchResponse = await apiClient.get(`/branches/${sale.branch_id}`);
        const branch = branchResponse.data.data;
        if (branch) {
          branchDetails = {
            branch_name: branch.attributes?.branch_name || branch.branch_name || ENV_CONFIG.DEFAULT_STORE_NAME,
            location: branch.attributes?.address || branch.address || ENV_CONFIG.DEFAULT_LOCATION,
            phone: branch.attributes?.phone || branch.phone || ENV_CONFIG.DEFAULT_CONTACT,
            email: branch.attributes?.email || branch.email || ''
          };
        }
      } catch (branchError) {
        console.error('Error fetching branch details:', branchError);
      }
    }
    
    // Calculate values according to structure.txt
    const subtotal = sale.sub_total || sale.sale_items?.reduce((sum, item) => sum + (item.total_price || 0), 0) || 0;
    const discountAmount = sale.discount_amount || 0;
    const discountPercentage = subtotal > 0 ? (discountAmount / subtotal) * 100 : 0;
    const medicineTotal = subtotal - discountAmount;
    // Consultation fee calculation commented out - not needed for now
    // const consultationFee = sale.total_amount - (subtotal - discountAmount);
    const consultationFee = 0; // Hardcoded to 0 - consultation fee feature disabled
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${sale.receipt_number}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #000; padding-bottom: 15px; margin-bottom: 20px; }
          .header-left { flex: 1; text-align: left; display: flex; align-items: center; }
          .logo { max-height: 60px; max-width: 120px; object-fit: contain; }
          .header-center { flex: 2; text-align: center; display: flex; flex-direction: column; align-items: center; justify-content: center; }
          .header-right { flex: 1; text-align: right; display: flex; flex-direction: column; align-items: flex-end; }
          .branch-name { font-size: 22px; font-weight: bold; margin: 0; margin-bottom: 5px; }
          .branch-location { font-size: 14px; color: #666; margin: 0; }
          .contact-info { 
            font-size: 12px; 
            color: #333; 
            margin: 3px 0; 
          }
          .email-link { 
            color: #1890ff; 
            text-decoration: none; 
          }
          .receipt-info { margin-bottom: 20px; }
          .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          .items-table th, .items-table td { border: 1px solid #000; padding: 8px; text-align: left; }
          .items-table th { background-color: #f0f0f0; }
          .total-section { text-align: right; margin-top: 20px; }
          .footer { text-align: center; margin-top: 30px; font-size: 12px; }
          .payment-section h3 { margin-top: 20px; margin-bottom: 10px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            <img src="${logoSrc}" alt="Logo" class="logo" />
          </div>
          <div class="header-center">
            <p class="branch-name">${branchDetails.branch_name}</p>
            <p class="branch-location">${branchDetails.location}</p>
          </div>
          <div class="header-right">
            <p class="contact-info" style="font-weight: bold;">Contact Information</p>
            <p class="contact-info">Phone: ${branchDetails.phone}</p>
            ${branchDetails.email ? `<p class="contact-info">Email: <a href="mailto:${branchDetails.email}" class="email-link">${branchDetails.email}</a></p>` : ''}
          </div>
        </div>
        <div class="receipt-info">
          <p><strong>Receipt Number:</strong> ${sale.receipt_number}</p>
          <p><strong>Date:</strong> ${receiptDate}</p>
          <p><strong>Customer:</strong> ${sale.patient_name || sale.customer_name || 'Walk-in Customer'}</p>
          ${(sale.patient_id || sale.customer_mobile) ? `<p><strong>Mobile:</strong> ${sale.patient_id || sale.customer_mobile}</p>` : ''}
        </div>
        <table class="items-table">
          <thead>
            <tr>
              <th>Medicine</th>
              <th>Qty</th>
              <th>Unit Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>

            ${(sale.sale_items || []).map((item: any) => `
              <tr>
                <td>${item.medicine_name || 'Unknown Medicine'}</td>
                <td>${item.quantity || 0}</td>
                <td>₹${parseFloat(item.unit_price || 0).toFixed(2)}</td>
                <td>₹${parseFloat(item.total_price || 0).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="total-section">
          <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
            <tr>
              <td style="padding: 8px 0; text-align: left; border-bottom: 1px solid #ddd;"><strong>Medicine Subtotal:</strong></td>
              <td style="padding: 8px 0; text-align: right; border-bottom: 1px solid #ddd;"><strong>₹${subtotal.toFixed(2)}</strong></td>
            </tr>
            ${discountAmount > 0 ? `
            <tr>
              <td style="padding: 8px 0; text-align: left; border-bottom: 1px solid #ddd;"><strong>Medicine Discount (${discountPercentage.toFixed(1)}%):</strong></td>
              <td style="padding: 8px 0; text-align: right; border-bottom: 1px solid #ddd;"><strong>-₹${discountAmount.toFixed(2)}</strong></td>
            </tr>
            ` : ''}
            <tr>
              <td style="padding: 8px 0; text-align: left; border-bottom: 1px solid #ddd;"><strong>Medicine Total:</strong></td>
              <td style="padding: 8px 0; text-align: right; border-bottom: 1px solid #ddd;"><strong>₹${medicineTotal.toFixed(2)}</strong></td>
            </tr>
            ${/* Consultation fee row commented out - not needed for now
            consultationFee > 0 ? `
            <tr>
              <td style="padding: 8px 0; text-align: left; border-bottom: 1px solid #ddd;"><strong>Consultation Fee:</strong></td>
              <td style="padding: 8px 0; text-align: right; border-bottom: 1px solid #ddd;"><strong>₹${consultationFee.toFixed(2)}</strong></td>
            </tr>
            ` : ''*/ ''}
            <tr style="background-color: #f5f5f5;">
              <td style="padding: 12px 0; text-align: left; font-size: 16px;"><strong>TOTAL AMOUNT:</strong></td>
              <td style="padding: 12px 0; text-align: right; font-size: 16px;"><strong>₹${Number(sale.total_amount || 0).toFixed(2)}</strong></td>
            </tr>
          </table>
        </div>
        
        ${sale.payment_methods && sale.payment_methods.length > 0 ? `
        <div class="payment-section">
          <h3>Payment Details</h3>
          <table class="items-table">
            <thead>
              <tr>
                <th>Payment Method</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${sale.payment_methods.map((payment: any) => `
                <tr>
                  <td>${payment.payment_method ? payment.payment_method.charAt(0).toUpperCase() + payment.payment_method.slice(1) : 'Cash'}</td>
                  <td>₹${parseFloat(payment.amount || 0).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}
        
        ${sale.notes ? `<div class="notes" style="margin-top: 20px;"><p><strong>Notes:</strong> ${sale.notes}</p></div>` : ''}
        
        <div class="footer">
          <p>Thank you for your purchase!</p>
          <p>Please keep this receipt for your records.</p>
        </div>
      </body>
      </html>
    `;
  };

  const totalAmount = cartItems.reduce((sum, item) => sum + item.total_price, 0);

  const salesColumns = [
    {
      title: 'Receipt #',
      dataIndex: 'receipt_number',
      key: 'receipt_number',
      render: (text: string) => (
        <Space>
          <FileTextOutlined />
          <strong>{text}</strong>
        </Space>
      ),
    },
    {
      title: 'Patient Name',
      dataIndex: 'patient_name',
      key: 'patient_name',
      render: (text: string) => (
        <Space>
          <UserOutlined />
          <strong>{text || 'Walk-in Patient'}</strong>
        </Space>
      ),
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount: number) => (
        <Space>
          ₹
          <strong>₹{amount}</strong>
        </Space>
      ),
    },
    {
      title: 'Payment Methods',
      dataIndex: 'payment_methods',
      key: 'payment_methods',
      render: (methods: any[]) => {
        if (!methods || methods.length === 0) {
          return <Tag color="green">CASH</Tag>;
        }
        return (
          <Space wrap>
            {methods.map((method, index) => (
              <Tag 
                key={index}
                color={method.payment_method === 'cash' ? 'green' : 
                       method.payment_method === 'card' ? 'blue' : 'orange'}
              >
                {method.payment_method?.toUpperCase() || 'CASH'}
                {method.amount && ` (₹${method.amount})`}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'sale_date',
      key: 'sale_date',
      render: (date: string) => formatForTable(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Sale) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewSale(record.sale_id)}
            />
          </Tooltip>
          <Tooltip title="Download Receipt">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadSale(record)}
            />
          </Tooltip>
          <Tooltip title="Print Receipt">
            <Button
              type="text"
              icon={<PrinterOutlined />}
              onClick={() => handlePrintSale(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];


  // New columns for medicine sales summary
  const medicineSalesSummaryColumns = [
    {
      title: 'Medicine Name',
      dataIndex: 'medicine_name',
      key: 'medicine_name',
      render: (text: string) => (
        <Space>
          <MedicineBoxOutlined />
          <strong>{text}</strong>
        </Space>
      ),
    },
    {
      title: 'Total Quantity Sold',
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      render: (quantity: number) => (
        <Tag color="green" style={{ fontSize: '14px', padding: '4px 8px' }}>
          {quantity} units
        </Tag>
      ),
      sorter: (a: any, b: any) => a.total_quantity - b.total_quantity,
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount: number) => (
        <strong style={{ color: '#1890ff', fontSize: '16px' }}>
          ₹{(amount || 0).toFixed(2)}
        </strong>
      ),
      sorter: (a: any, b: any) => a.total_amount - b.total_amount,
    },
    {
      title: 'Number of Sales',
      dataIndex: 'sale_count',
      key: 'sale_count',
      render: (count: number) => (
        <Badge count={count} showZero color="blue" />
      ),
      sorter: (a: any, b: any) => a.sale_count - b.sale_count,
    },
  ];

  // Fetch Total Sales (respecting selected date range if provided) via backend statistics
  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Total Sales (optional date range)
        const statsParams: any = {};
        if (dateRange[0]) statsParams['sale_from'] = dateRange[0];
        if (dateRange[1]) statsParams['sale_to'] = dateRange[1];
        if (branchId) statsParams['branch_id'] = branchId;
        const statsResp = await apiClient.get('/sales/reports/statistics', { params: statsParams });
        const total = Number(statsResp.data?.data?.total_revenue || 0);
        setTotalRevenue(total);
      } catch (e) {
        console.error('Failed to fetch sales statistics', e);
        setTotalRevenue(0);
      }
    };
    fetchStats();
  }, [dateRange, branchId]);
  
  // Debug logging to help identify issues
  console.log('Sales data:', sales);
  console.log('Total sales calculated (from stats):', totalRevenue);
  
  

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          {branchId ? 'Branch Sales Management' : 'Seller-Branch Sales Management'}
        </Title>
        {!branchId && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleNewSale}
            size="large"
          >
            New Transaction
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)',
              border: '1px solid #e6f7ff'
            }}
          >
            <Statistic
              title="Total Sales"
              value={isNaN(totalRevenue) ? 0 : totalRevenue}
              prefix="₹"
              precision={2}
              valueStyle={{ color: '#1d39c4', fontSize: 28 }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Cash Revenue"
              value={Number(paymentTotals.cash || 0)}
              prefix="₹"
              precision={2}
              valueStyle={{ color: '#389e0d' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Card Revenue"
              value={Number(paymentTotals.card || 0)}
              prefix="₹"
              precision={2}
              valueStyle={{ color: '#1d39c4' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="UPI Revenue"
              value={Number(paymentTotals.upi || 0)}
              prefix="₹"
              precision={2}
              valueStyle={{ color: '#d48806' }}
            />
          </Card>
        </Col>
      </Row>


      {/* New Sale Modal */}
      <Modal
        title="Process New Seller-Branch Transaction"
        open={isModalVisible}
        onOk={handleProcessSale}
        onCancel={() => {
          setIsModalVisible(false);
          setCartItems([]);
          form.resetFields();
        }}
        width={1000}
        okText="Process Transaction"
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="branch_id"
                label="Branch"
                rules={[{ required: true, message: 'Please select branch' }]}
              >
                <Select placeholder="Select branch">
                  {branches.map(branch => (
                    <Option key={branch.branch_id} value={branch.branch_id}>
                      {branch.branch_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="receipt_number"
                label="Receipt Number"
                rules={[{ required: true, message: 'Please enter receipt number' }]}
              >
                <Input placeholder="Enter receipt number" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="payment_method"
                label="Payment Method"
                rules={[{ required: true, message: 'Please select payment method' }]}
                initialValue="cash"
              >
                <Select placeholder="Select payment method">
                  <Option value="cash">Cash</Option>
                  <Option value="card">Card</Option>
                  <Option value="upi">UPI</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <Input.TextArea rows={2} placeholder="Enter any notes" />
          </Form.Item>
        </Form>

        <Divider>Add Items to Cart</Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Select
              placeholder="Select medicine"
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
              onSelect={(value) => {
                const medicine = medicines.find(m => m.medicine_id === value);
                if (medicine) {
                  handleAddToCart(medicine, 1);
                }
              }}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
              }
            >
              {medicines.map(medicine => (
                <Option key={medicine.medicine_id} value={medicine.medicine_id}>
                  {medicine.medicine_name} - ₹{medicine.unit_price}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Divider>
          <Space>
            <span>Cart Items</span>
            {cartItems.length > 0 && (
              <Button
                type="link"
                danger
                size="small"
                onClick={handleClearCart}
                icon={<DeleteOutlined />}
              >
                Clear Cart
              </Button>
            )}
          </Space>
        </Divider>

        {cartItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            No items in cart
          </div>
        ) : (
          <List
            dataSource={cartItems}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveFromCart(item.medicine_id)}
                  >
                    Remove
                  </Button>
                ]}
              >
                <List.Item.Meta
                  title={item.medicine_name}
                  description={`Quantity: ${item.quantity} | Unit Price: ₹${item.unit_price}`}
                />
                <div>
                  <strong>₹{item.total_price}</strong>
                </div>
              </List.Item>
            )}
          />
        )}

        <Divider>
          <Space>
            <strong>Total Amount: ₹{totalAmount}</strong>
          </Space>
        </Divider>
      </Modal>

      {/* Sale Details Modal */}
      <Modal
        title="Sale Details"
        open={saleModalVisible}
        onCancel={() => setSaleModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedSale && (
          <div>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Receipt Number">
                <strong>{selectedSale.receipt_number}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Branch">
                <Tag icon={<BankOutlined />}>{selectedSale.branch_name}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Sale Date">
                {formatForReceipt(selectedSale.createdAt || selectedSale.sale_date)}
              </Descriptions.Item>
              {(selectedSale.customer_name || (selectedSale.sale_items && selectedSale.sale_items[0]?.patient_name)) && (
                <Descriptions.Item label="Customer Name">
                  <UserOutlined /> {selectedSale.customer_name || selectedSale.sale_items?.[0]?.patient_name || 'Walk-in Customer'}
                </Descriptions.Item>
              )}
              {selectedSale.customer_mobile && (
                <Descriptions.Item label="Customer Mobile">
                  {selectedSale.customer_mobile}
                </Descriptions.Item>
              )}
              {(selectedSale.patient_id || (selectedSale.sale_items && selectedSale.sale_items[0]?.patient_id)) && (
                <Descriptions.Item label="Patient ID">
                  {selectedSale.patient_id || selectedSale.sale_items?.[0]?.patient_id}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Payment Method">
                <Tag color={selectedSale.payment_method === 'cash' ? 'green' : selectedSale.payment_method === 'card' ? 'blue' : 'orange'}>
                  {selectedSale.payment_method?.toUpperCase() || 'CASH'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Medicine Subtotal">
                <strong>₹{Number(selectedSale.sub_total || 0).toFixed(2)}</strong>
              </Descriptions.Item>
              {(selectedSale.discount_amount || 0) > 0 && (
                <Descriptions.Item label="Medicine Discount">
                  <strong>-₹{Number(selectedSale.discount_amount || 0).toFixed(2)}</strong>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Medicine Total">
                <strong>₹{Number((selectedSale.sub_total || 0) - (selectedSale.discount_amount || 0)).toFixed(2)}</strong>
              </Descriptions.Item>
              {/* Consultation fee display commented out - not needed for now */}
              {/* {(() => {
                const consultationFee = (selectedSale.total_amount || 0) - ((selectedSale.sub_total || 0) - (selectedSale.discount_amount || 0));
                return consultationFee > 0 ? (
                  <Descriptions.Item label="Consultation Fee">
                    <strong>₹{consultationFee.toFixed(2)}</strong>
                  </Descriptions.Item>
                ) : null;
              })()} */}
              <Descriptions.Item label="Total Amount">
                <strong style={{ fontSize: '18px' }}>
                  ₹{Number(selectedSale.total_amount || 0).toFixed(2)}
                </strong>
              </Descriptions.Item>
              {selectedSale.payment_methods && selectedSale.payment_methods.length > 0 && (
                <Descriptions.Item label="Payment Methods">
                  <div>
                    {selectedSale.payment_methods.map((payment: any, index: number) => (
                      <div key={index} style={{ marginBottom: '4px' }}>
                        <strong>{payment.payment_method?.toUpperCase() || 'CASH'}:</strong> ₹{Number(payment.amount || 0).toFixed(2)}
                      </div>
                    ))}
                  </div>
                </Descriptions.Item>
              )}
              {selectedSale.notes && (
                <Descriptions.Item label="Notes">
                  {selectedSale.notes}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider>Items Sold</Divider>
            
            <List
              dataSource={selectedSale.sale_items || []}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <div>
                        <strong>{item.medicine_name}</strong>
                        {item.manufacturer && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {item.manufacturer}
                          </div>
                        )}
                      </div>
                    }
                    description={
                      <div>
                        <div>Quantity: {item.quantity}</div>
                        <div>Unit Price: ₹{Number(item.unit_price || 0).toFixed(2)}</div>
                        {item.discount_percentage && item.discount_percentage > 0 && (
                          <div>Discount: {item.discount_percentage}%</div>
                        )}
                      </div>
                    }
                  />
                  <div>
                    <strong>₹{Number(item.total_price || 0).toFixed(2)}</strong>
                  </div>
                </List.Item>
              )}
            />
          </div>
        )}
      </Modal>

      {/* Sliding Panel on Main Page */}
      <Card
        style={{
          width: '100%',
          minHeight: '600px',
        }}
      >
        <Tabs
          activeKey={activePanelTab}
          onChange={setActivePanelTab}
          items={[
            {
              key: 'sale-history',
              label: (
                <span>
                  <FileTextOutlined />
                  Sale History
                </span>
              ),
              children: (
                <div style={{ padding: '24px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <Row gutter={[8, 8]}>
                      <Col xs={24} md={8}>
                        <Input
                          placeholder="Search sales by receipt number, branch, customer..."
                          prefix={<SearchOutlined />}
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          allowClear
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <DatePicker.RangePicker
                          style={{ width: '100%' }}
                          onChange={(values) => {
                            const start = values?.[0]?.format('YYYY-MM-DD');
                            const end = values?.[1]?.format('YYYY-MM-DD');
                            setDateRange([start, end]);
                          }}
                          allowEmpty={[true, true]}
                        />
                      </Col>
                      
                    </Row>
                  </div>
                  {Array.isArray(sales) && sales.length === 0 && !loading ? (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '40px 20px',
                      background: '#fafafa',
                      borderRadius: '8px',
                      border: '1px dashed #d9d9d9'
                    }}>
                      <ShoppingCartOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                      <div style={{ fontSize: '16px', color: '#666', marginBottom: '8px' }}>
                        No sales recorded
                      </div>
                      <div style={{ fontSize: '14px', color: '#999' }}>
                        {branchId ? 'Sales will appear here once transactions are made for this branch.' : 'Sales will appear here once transactions are made.'}
                      </div>
                    </div>
                  ) : (
                    <Table
                      columns={salesColumns}
                      dataSource={Array.isArray(sales) ? sales : []}
                      rowKey="sale_id"
                      loading={loading}
                      scroll={{ x: 700, y: 400 }}
                      size="small"
                      pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} sales`,
                        onChange: (page, pageSize) => {
                          dispatch(fetchSales({ 
                            page, 
                            limit: pageSize, 
                            search: debouncedSearchText, 
                            branchId,
                            start_date: dateRange[0] || '',
                            end_date: dateRange[1] || ''
                          }));
                        },
                        onShowSizeChange: (current, size) => {
                          dispatch(fetchSales({ 
                            page: 1, 
                            limit: size, 
                            search: debouncedSearchText, 
                            branchId,
                            start_date: dateRange[0] || '',
                            end_date: dateRange[1] || ''
                          }));
                        },
                      }}
                    />
                  )}
                </div>
              ),
            },
            {
              key: 'medicine-sales',
              label: (
                <span>
                  <MedicineBoxOutlined />
                  Medicine Sales
                </span>
              ),
              children: (
                <div style={{ padding: '24px' }}>
                  <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                    Showing aggregated sales data for all medicines
                  </div>
                  {medicineSalesSummary.length === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '40px 20px',
                      background: '#fafafa',
                      borderRadius: '8px',
                      border: '1px dashed #d9d9d9'
                    }}>
                      <MedicineBoxOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                      <div style={{ fontSize: '16px', color: '#666', marginBottom: '8px' }}>
                        No medicine sales recorded
                      </div>
                      <div style={{ fontSize: '14px', color: '#999' }}>
                        {branchId ? 'No medicine sales have been recorded for this branch.' : 'No medicine sales have been recorded.'}
                      </div>
                    </div>
                  ) : (
                    <Table
                      columns={medicineSalesSummaryColumns}
                      dataSource={Array.isArray(medicineSalesSummary) ? medicineSalesSummary : []}
                      rowKey="medicine_name"
                      loading={loading}
                      scroll={{ x: 600, y: 400 }}
                      size="small"
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} medicines`,
                      }}
                    />
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default Sales;
